import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { teamLabel } from "@/components/manage/matchUtils";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Minus, Plus, AlertTriangle, Play } from "lucide-react";

// endpoint builder: host uses /matches/:id/... ; code-based passes body {code}
export default function ResultDialog({ open, onOpenChange, match, teamMap, onDone, mode = "host", code }) {
  const [a, setA] = useState(match?.score_a || 0);
  const [b, setB] = useState(match?.score_b || 0);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) { setA(match?.score_a || 0); setB(match?.score_b || 0); setConfirming(false); }
  }, [open, match]);

  if (!match) return null;
  const nameA = teamLabel(match, "a", teamMap);
  const nameB = teamLabel(match, "b", teamMap);
  const ready = match.team_a_id && match.team_b_id;

  const call = async (action, body) => {
    if (mode === "host") return api.post(`/matches/${match.id}/${action}`, body);
    return api.post(`/access/${mode}/matches/${match.id}/${action}`, { ...body, code });
  };

  const saveScore = async () => {
    setBusy(true);
    try {
      if (match.status === "pending") await call("start", {});
      await call("score", { score_a: a, score_b: b });
      toast.success("Score saved");
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitFinal = async () => {
    setBusy(true);
    try {
      await call("submit", { score_a: a, score_b: b });
      toast.success("Final result submitted");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="result-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{confirming ? "Confirm final result" : "Enter score"}</DialogTitle>
        </DialogHeader>

        {!ready ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Both teams must be decided before entering a score.</p>
        ) : !confirming ? (
          <div className="space-y-4">
            <ScoreRow name={nameA} value={a} set={setA} side="a" />
            <div className="text-center text-xs font-bold text-muted-foreground">VS</div>
            <ScoreRow name={nameB} value={b} set={setB} side="b" />
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button variant="secondary" className="w-full" onClick={saveScore} disabled={busy} data-testid="save-score-button">
                {match.status === "pending" ? <><Play className="mr-1.5 h-4 w-4" /> Start & save score</> : "Save score (live)"}
              </Button>
              <Button className="w-full" onClick={() => setConfirming(true)} disabled={busy} data-testid="finish-match-button">
                Finish match
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border bg-muted/40 p-4 text-center">
              <div className="font-semibold">{nameA}</div>
              <div className="font-heading text-5xl font-black">{a} <span className="text-muted-foreground">:</span> {b}</div>
              <div className="font-semibold">{nameB}</div>
            </div>
            <div className="flex items-start gap-2 rounded-lg bg-accent/10 p-3 text-sm text-accent">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Once submitted, this result <strong>cannot be changed</strong> by anyone.</span>
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button className="w-full bg-accent text-accent-foreground hover:bg-accent/90" onClick={submitFinal} disabled={busy} data-testid="submit-final-result-btn">
                {busy ? "Submitting…" : "Submit final result"}
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => setConfirming(false)} disabled={busy} data-testid="back-to-score-button">
                Back
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScoreRow({ name, value, set, side }) {
  return (
    <div className="flex items-center justify-between rounded-xl border p-3">
      <span className="truncate font-semibold">{name}</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => set(Math.max(0, value - 1))} data-testid={`score-${side}-minus`}>
          <Minus className="h-4 w-4" />
        </Button>
        <span className="w-8 text-center font-heading text-2xl font-black" data-testid={`score-${side}-value`}>{value}</span>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => set(value + 1)} data-testid={`score-${side}-plus`}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
